import { DateTime } from 'luxon';
import type { DrillData } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { getOnlyTimeSeconds, getUTCSeconds, getUTCSecondsAtStartOfDay } from '@/utils';
import { DateMode, type FormField, type FormFieldChoice, type FormFieldType, type NetworkLocationData } from './form';
import { OTHER_CHOICE_CODE } from './entry';
import { getMatrixValues, isMatrixAnswered } from './matrixAnswer';

/**
 * What each field type writes to the API (`toWire`) and whether that is an answer (`isAnswered`),
 * in one table keyed by field type (ENG-9018 decisions 1, 6 and 7). The rule is **Answered** in
 * CONTEXT.md: a field is answered when the value under its own key is not absent, `''` or `null`, so
 * `isAnswered` is read off `toWire`, and the server applies the same rule to what it stores. The
 * matrix has its own rule over its cells, in `matrixAnswer.ts`. ADR 0009 records the encodings that
 * changed for it.
 *
 * There is no reader here: `getFieldValue` in `effects.ts` stays the reader conditions use.
 */

/**
 * The keys one field writes into the save body: its identifier, and `<id>_data` for a datetime's zone and for the
 * override mark on a field the reader may override.
 */
export type WireAnswer = Record<string, string | number>;

interface FieldAnswerRow {
  toWire: (field: FormField) => WireAnswer;
  isAnswered: (field: FormField) => boolean;
  /** Set on a field the entry adds for itself, which the save body leaves out. */
  clientOnly?: true;
}

/** NaN counts as blank because `JSON.stringify` sends it as `null`. */
const isWireAnswer = (value: string | number | null | undefined) =>
  !(value === undefined || value === null || value === '' || Number.isNaN(value));

/** A field that writes one key, and unless the row says otherwise is answered by what it writes there. */
const oneKey = (
  encode: (field: FormField) => string | number,
  isAnswered: (field: FormField) => boolean = field => isWireAnswer(encode(field))
): FieldAnswerRow => ({
  toWire: field => ({ [field.identifier]: encode(field) }),
  isAnswered
});

// The value the item left in `value`, which is what goes when a type has nothing of its own set.
const valueOrBlank = (field: FormField) => field.value ? field.value : '';

const numberOrBlank = (field: FormField) =>
  field.numberValue !== undefined && field.numberValue !== null ? field.numberValue : '';

const choices = (field: FormField) => {
  if (field.options.maximumChoicesCount == 1) {
    const value = (field.choiceValue as FormFieldChoice)?.value;
    if (!value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
      return field.otherChoiceValue?.otherValue ?? '';
    }
    return value;
  }

  const selectedChoices = (field.options.choices ?? []).filter(choice => choice.checked).map(choice => `${choice.value}`);
  if (field.options.allowOther && field.otherChoiceValue?.checked) {
    selectedChoices.push(field.otherChoiceValue?.otherValue ?? '');
  }
  return selectedChoices.join(',');
};

// A single dropdown with nothing picked writes `undefined`, which `buildEntryPayload` leaves out of the
// body, so the stored answer is kept, even when the reader cleared it. Writing '' instead would also
// erase a stored value that matches no current choice, since that loads as nothing picked too.
const dropdown = (field: FormField) => field.options.maximumChoicesCount == 1 ?
  (field.choiceValue as FormFieldChoice)?.value :
  ((field.choiceValue as FormFieldChoice[]) ?? []).map(choice => `${choice.value}`).join(',');

const dateSeconds = (field: FormField, dateValue: Date) => {
  if (field.options.mode === DateMode.DATE) {
    return getUTCSecondsAtStartOfDay(dateValue);
  }
  if (field.options.mode === DateMode.DATE_TIME) {
    return field.options.absoluteDate ? getUTCSeconds(dateValue) : DateTime.fromJSDate(dateValue).toSeconds();
  }
  return getOnlyTimeSeconds(dateValue);
};

const dataKey = (field: FormField) => `${field.identifier}_data`;

/**
 * The zone a datetime was recorded in, absolute or not, without the override mark an entry saved before ENG-9047 kept
 * in it: `toWire` writes the mark, and only while the field still carries one.
 */
const recordedZone = (field: FormField) => {
  if (!field.timezoneRecorded) {
    return undefined;
  }
  const zone = { ...field.timezoneRecorded };
  delete zone.overrideCalculatedValue;
  return zone;
};

/** A datetime also writes the zone it was recorded in under `<id>_data`. */
const dateToWire = (field: FormField): WireAnswer => {
  if (!field.dateValue) {
    return { [field.identifier]: valueOrBlank(field) };
  }
  const wire: WireAnswer = { [field.identifier]: dateSeconds(field, field.dateValue) };
  if (field.options.mode === DateMode.DATE_TIME) {
    wire[dataKey(field)] = JSON.stringify(recordedZone(field));
  }
  return wire;
};

const date: FieldAnswerRow = {
  toWire: dateToWire,
  isAnswered: field => isWireAnswer(dateToWire(field)[field.identifier])
};

const location = (field: FormField) => {
  if (!field.locationValue) {
    return valueOrBlank(field);
  }
  const { latitude, longitude, accuracy } = field.locationValue.coords;
  const locationValue: NetworkLocationData = { latitude, longitude, accuracy, timestamp: field.locationValue.timestamp };
  return JSON.stringify(locationValue);
};

/** An emptied list (the last file removed) goes as `''`, not `"[]"` (ADR 0009). */
const files = (field: FormField) => {
  if (!field.fileIds) {
    return valueOrBlank(field);
  }
  return field.fileIds.length > 0 ? JSON.stringify(field.fileIds) : '';
};

const matrix = (field: FormField) => {
  if (!field.matrixTableRowValues) {
    return valueOrBlank(field);
  }
  const matrixValues = getMatrixValues(field.matrixTableRowValues);
  return matrixValues ? JSON.stringify(matrixValues) : '';
};

const drilldown = (field: FormField) =>
  ((field.drillValue as DrillData[] | undefined) ?? []).map(level => level.label).join(',');

/** A unit needs its number: an inequality or a unit on its own goes as `''` (ADR 0009). */
const unit = (field: FormField) => {
  if (!isWireAnswer(field.numberValue)) {
    return '';
  }
  return JSON.stringify({
    value: field.numberValue,
    unit: field.value,
    inequality: (field.choiceValue as FormFieldChoice)?.value
  });
};

/** The submit button and branding the entry adds for itself. Neither is a question on the form. */
const clientOnly: FieldAnswerRow = { toWire: () => ({}), isAnswered: () => false, clientOnly: true };

/*
 * Keyed by the enum's string values rather than `[FormFieldType.NUMBER]`, so the table builds in any
 * load order: while `form.ts` imported this module, a caller entering the models through the barrel
 * built it before `FormFieldType` existed. `Record<FormFieldType, …>` still fails `tsc` until a new
 * type has a row.
 */
const FIELD_ANSWERS: Record<FormFieldType, FieldAnswerRow> = {
  Text: oneKey(valueOrBlank),
  Number: oneKey(field => field.options.range ? valueOrBlank(field) : numberOrBlank(field)),
  Slider: oneKey(numberOrBlank),
  Choice: oneKey(choices),
  Dropdown: oneKey(dropdown),
  Date: date,
  Location: oneKey(location),
  File: oneKey(files),
  Signature: oneKey(field => field.signatureValue ? JSON.stringify(field.signatureValue) : ''),
  Matrix: oneKey(matrix, isMatrixAnswered),
  Drilldown: oneKey(drilldown),
  Unit: oneKey(unit),
  // The code as the web's box saves it, typed or pasted (ENG-9072). A code of only spaces is no code, so it is
  // trimmed, unlike text.
  Barcode: oneKey(valueOrBlank, field => valueOrBlank(field).trim() !== ''),
  Title: oneKey(valueOrBlank),
  Section: oneKey(valueOrBlank),
  Branding: clientOnly,
  SubmitButton: clientOnly
};

/**
 * `<id>_data` on a field the form lets the reader override (ADR 0009, amended by ENG-9047). An answered field that
 * carries the mark writes `{"overrideCalculatedValue":true}`, in a datetime's zone where it has one. Without the mark
 * it writes the zone alone, or `''`, because PATCH merges the body over the stored items, and a key left out would keep
 * a mark the reader took off.
 */
const withDataKey = (field: FormField, wire: WireAnswer, answered: boolean): WireAnswer => {
  // Only a row that wrote a zone has one to keep: a date or a time keeps none, whatever it was loaded with.
  const zone = dataKey(field) in wire ? recordedZone(field) : undefined;
  if (field.overrideCalculatedValue === true && answered) {
    return { ...wire, [dataKey(field)]: JSON.stringify({ ...zone, overrideCalculatedValue: true }) };
  }
  return { ...wire, [dataKey(field)]: zone ? JSON.stringify(zone) : '' };
};

/**
 * Whether the form lets the reader override the field's calculation. Here rather than in `effects.ts`, so this module
 * needn't import that one, and `toWire` and the Assign checks in `effects.ts` read the one rule.
 */
export const allowsOverride = (field: FormField): boolean => !!field.options?.overrideCalculation;

export const toWire = (field: FormField): WireAnswer => {
  const row = FIELD_ANSWERS[field.type];
  const wire = row.toWire(field);
  return allowsOverride(field) ? withDataKey(field, wire, row.isAnswered(field)) : wire;
};

export const isAnswered = (field: FormField): boolean => FIELD_ANSWERS[field.type].isAnswered(field);

/** Whether the field is one the entry added for itself, which the save body leaves out. */
export const isClientOnly = (field: FormField): boolean => FIELD_ANSWERS[field.type].clientOnly === true;
